import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BellIcon, CreditCardIcon, LayoutListIcon, LogOutIcon, MenuIcon, SettingsIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { QuotaMeter } from './QuotaMeter';
import { Wordmark } from './Wordmark';
const navItems = [{
  to: '/app',
  label: 'Monitors',
  icon: LayoutListIcon,
  end: true
}, {
  to: '/app/alerts',
  label: 'Alert history',
  icon: BellIcon,
  end: false
}, {
  to: '/app/notifications',
  label: 'Notifications',
  icon: ShieldCheckIcon,
  end: false
}, {
  to: '/app/billing',
  label: 'Billing',
  icon: CreditCardIcon,
  end: false
}, {
  to: '/app/settings',
  label: 'Settings',
  icon: SettingsIcon,
  end: false
}];
function SidebarContent({
  onNavigate
}: {
  onNavigate?: () => void;
}) {
  const {
    profile,
    plan,
    servicesUsed,
    serviceLimit
  } = useAccount();
  const {
    signOut
  } = useAuth();
  const navigate = useNavigate();
  return <div className="flex h-full flex-col gap-6 p-4" data-id="element-110" data-mp="1mdscyqezb172">
      <Link to="/" className="flex items-center gap-2 rounded-control px-2 py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-111" data-mp="1mf3cuf5azosw">
        <Wordmark tone="dark" size="sm" data-id="element-112" data-mp="1wadf93zm5y77" />
      </Link>

      <nav aria-label="Account" className="flex flex-col gap-0.5" data-id="element-113" data-mp="2a2tnqkbaaofi">
        {navItems.map(item => <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} className={({
        isActive
      }) => `flex items-center gap-2.5 rounded-control px-2.5 py-2 text-[13px] font-medium transition-colors duration-150 ease-snap ${isActive ? 'bg-shell-raised text-white' : 'text-white/60 hover:bg-shell-raised/60 hover:text-white'}`} data-id="element-114" data-mp="2551mgzor1pyn">
            <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-115" data-mp="1adc67x8vrvis" />
            {item.label}
          </NavLink>)}
      </nav>

      <div className="mt-auto space-y-3" data-id="element-116" data-mp="1eefjcbzjndgb">
        <QuotaMeter used={servicesUsed} limit={serviceLimit} planName={plan.name} tone="shell" data-id="element-117" data-mp="1jdihdch3wjes" />
        <div className="flex items-center gap-2.5 rounded-card border border-shell-line px-3 py-2.5" data-id="element-118" data-mp="1odwmomrbpf94">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-shell-raised text-[11px] font-semibold text-white" data-id="element-119" data-mp="1tawscwk9gfzp">
            {profile.name.split(' ').map(part => part[0]).join('')}
          </span>
          <span className="min-w-0" data-id="element-120" data-mp="271rvrtbzq4vw">
            <span className="block truncate text-[13px] font-medium text-white" data-id="element-121" data-mp="274xix3126woz">
              {profile.name}
            </span>
            <span className="block truncate text-[11px] text-white/50" data-id="element-122" data-mp="2h0r4fbwra0iw">
              {profile.email}
            </span>
          </span>
        </div>
        <button type="button" onClick={() => {
        signOut();
        navigate('/login');
      }} className="flex w-full items-center gap-2.5 rounded-control px-2.5 py-2 text-[13px] font-medium text-white/60 transition-colors duration-150 ease-snap hover:bg-shell-raised/60 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-bright" data-id="element-123" data-mp="5f8hvu69awr2">
          <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-124" data-mp="j4irj3jhx6qg" />
          Sign out
        </button>
      </div>
    </div>;
}
export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return <div className="flex min-h-full w-full bg-canvas" data-id="element-125" data-mp="9e7hr0d4s3bn">
      <aside className="sticky top-0 hidden h-screen w-[228px] shrink-0 bg-shell lg:block" data-id="element-126" data-mp="16umsf35ausdr">
        <SidebarContent data-id="element-127" data-mp="123wn1pwvipj2" />
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-40 lg:hidden" data-id="element-128" data-mp="122d8c3qv8mje">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setMobileOpen(false)} aria-hidden="true" data-id="element-129" data-mp="mldjb83cuvjh" />
          <div className="absolute left-0 top-0 h-full w-[248px] bg-shell shadow-pop" data-id="element-130" data-mp="riwufz4ras77">
            <SidebarContent onNavigate={() => setMobileOpen(false)} data-id="element-131" data-mp="cpjw8us0fjng" />
          </div>
        </div>}

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-132" data-mp="hn256l611yvg">
        <div className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-surface px-4 lg:hidden" data-id="element-133" data-mp="15c4m1qsu7now">
          <button type="button" onClick={() => setMobileOpen(open => !open)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-line-strong text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-134" data-mp="1aa67gidqjz3j">
            {mobileOpen ? <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-135" data-mp="1t3tnbwnmzmhz" /> : <MenuIcon className="h-4 w-4" aria-hidden="true" data-id="element-136" data-mp="3dqjlh6egmgay" />}
          </button>
          <Wordmark size="sm" showSubline={false} data-id="element-137" data-mp="38t17v2phzcwm" />
        </div>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8" data-id="element-138" data-mp="3nh5sic841yi1">
          <div className="mx-auto w-full max-w-[1080px]" data-id="element-139" data-mp="3ijcnvnmghcjv">
            <Outlet data-id="element-140" data-mp="3ijw6z7nue6zh" />
          </div>
        </main>
      </div>
    </div>;
}